import React, { useEffect } from "react";
import SurveyContent from "./SurveyContent";
import SurveyNavigation from "./SurveyNavigation";
import { useSurveys } from "../services/hooks/useSurveys";
import validateSurveyContentResponse from "../services/lib/surveys/validateSurveyContentResponse";
import Loader from "./Loader";
import Error from "./Error";
import RestrictionError from "./RestrictionError";
import Countdown from "react-countdown";
import { SURVEY_CONTENT_TYPES } from "../services/lib/surveys/constants";
import time from "../services/time/time";
import { useTimezone } from "../services/hooks/useTimezone";
import { useAddToCalendar } from "../services/hooks/useAddToCalendar";

export default React.memo(function Survey() {
  const {
    surveyDisplayMessage,
    isSavingActiveSurveyResponse,
    shouldShowSurveyCountdownTimer,
    surveyStartTime,
    surveyEndTime,
    surveyContentError,
    surveyError,
    surveyStatus,
    activeSurvey,
    activeContent,
    currentStep,
    setCurrentStep,
    setSurveyContentError,
    setActiveContentId,
    setActiveContentResponse,
    setActiveContentOptionIsSelected,
    saveActiveSurveyResponse,
  } = useSurveys();

  const { timezone } = useTimezone();

  const { addToCalendar } = useAddToCalendar();

  const onClickBack = (e) => {
    e?.preventDefault();

    setCurrentStep({
      currentStep: currentStep - 1,
    });
  };

  const onClickNext = (e) => {
    e?.preventDefault();

    //perform validations if any
    const { isValidResponse, responseError } =
      validateSurveyContentResponse(activeContent);

    if (responseError) {
      setSurveyContentError({
        surveyContentError: responseError,
      });
    }

    if (isValidResponse) {
      if (currentStep + 1 <= activeSurvey.contents.length) {
        setCurrentStep({
          currentStep: currentStep + 1,
        });
      }
    }
  };

  const onClickSubmit = (e) => {
    e?.preventDefault();

    saveActiveSurveyResponse();
  };

  const onClickThankYouBtn = (e) => {
    e?.preventDefault();

    const { hasButton, buttonHasLink, buttonLink } = activeContent;

    if (hasButton && buttonHasLink && buttonLink) {
      window.open(buttonLink, "_blank");
    }
  };

  const onResponse = (response) => {
    setActiveContentResponse({
      response,
    });
  };

  const onSelectOption = (data) => {
    setActiveContentOptionIsSelected(data);
  };

  const handleAddToCalendar = () => {
    addToCalendar({
      name: "Chic Beauty Box - Mar / Apr Choice Event",
      description:
        "Hooray! We're giving everyone a fair and equal opportunity to choose the items they want in their Mar - Apr boxes. So come on time, and come prepared to select your favs before they're gone.",
      startDate: time.unix(surveyStartTime).tz(timezone).format("YYYY-MM-DD"),
      endDate: surveyEndTime
        ? time.unix(surveyEndTime).tz(timezone).format("YYYY-MM-DD")
        : time
            .unix(surveyStartTime)
            .tz(timezone)
            .add(1, "d")
            .format("YYYY-MM-DD"),
      startTime: time.unix(surveyStartTime).tz(timezone).format("HH:mm"),
      endTime: surveyEndTime
        ? time.unix(surveyEndTime).tz(timezone).format("HH:mm")
        : time.unix(surveyStartTime).tz(timezone).add(1, "d").format("HH:mm"),
      options: [
        "Apple",
        "Google",
        "iCal",
        "Microsoft365",
        "Outlook.com",
        "MicrosoftTeams",
        "Yahoo",
      ],
      timeZone: timezone,
      trigger: "click",
      iCalFileName: "CBB-Choice-Event",
    });
  };

  useEffect(() => {
    if (activeSurvey && !currentStep) {
      setCurrentStep({
        currentStep: 1,
      });
    } else if (activeSurvey && currentStep) {
      setActiveContentId({
        activeContentId: activeSurvey?.contents[currentStep - 1]?.id,
      });
    }
  }, [activeSurvey, currentStep]);

  if (
    (surveyStatus === "pending" || !activeSurvey || !activeContent) &&
    !surveyDisplayMessage
  ) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-12 mt-15">
            <Loader absoluteCenter />
          </div>
        </div>
      </div>
    );
  }

  if (surveyError) {
    return <Error statusCode={404} />;
  }

  if (shouldShowSurveyCountdownTimer) {
    return (
      <Countdown
        date={surveyStartTime * 1000}
        renderer={({ days, hours, minutes, seconds, completed }) => {
          if (completed) {
            // Render a completed state
            return null;
          } else {
            return (
              <div
                className="container d-flex justify-content-center align-items-center"
                style={{ height: "30rem" }}
              >
                <div className="d-flex flex-column justify-content-center align-items-center">
                  <span
                    className="quartz-regular d-none d-lg-block"
                    style={{ fontSize: "10rem" }}
                  >
                    {days ? `${days}:` : ""}
                    {hours}:{minutes}:{seconds}
                  </span>

                  <span
                    className="quartz-regular d-lg-none"
                    style={{ fontSize: "5rem" }}
                  >
                    {days ? `${days}:` : ""}
                    {hours}:{minutes}:{seconds}
                  </span>

                  <div className="h6 text-center">
                    This event hasn't started yet. Check back on{" "}
                    <span className="text-primary">
                      {time
                        .unix(surveyStartTime)
                        .format("MMMM DD, YYYY [at] hh:mm a")}
                      .
                    </span>
                  </div>

                  <button
                    className="btn btn-outline-dark"
                    onClick={handleAddToCalendar}
                  >
                    Add to Calendar
                  </button>
                </div>
              </div>
            );
          }
        }}
      />
    );
  }

  if (surveyDisplayMessage) {
    return <RestrictionError message={surveyDisplayMessage} />;
  }

  return (
    <div className="container">
      {activeSurvey.surveyName && (
        <div className="row">
          <div className="col-12">
            <h3 className="text-center">{activeSurvey.surveyName}</h3>
          </div>
        </div>
      )}

      <div className="survey-fullscreen">
        <div className="quiz">
          <SurveyContent
            {...activeContent}
            onResponse={onResponse}
            onSelectOption={onSelectOption}
            error={surveyContentError}
          />

          <SurveyNavigation
            onClickBack={onClickBack}
            onClickNext={onClickNext}
            onClickSubmit={onClickSubmit}
            onClickThankYouBtn={onClickThankYouBtn}
            hasThankYouBtn={
              activeContent.type === SURVEY_CONTENT_TYPES.ENDING_SCREEN
                ? activeContent.hasButton
                : false
            }
            currentStep={currentStep}
            totalSteps={activeSurvey.contents.length}
            nextBtnText={
              activeContent.ui.button.text
                ? activeContent.ui.button.text
                : activeContent.ui.button.default
            }
            thankYouBtnText={
              activeContent.ui.button.text
                ? activeContent.ui.button.text
                : activeContent.ui.button.default
            }
            disabled={isSavingActiveSurveyResponse}
          />
        </div>
      </div>
    </div>
  );
});
